<template>
    <div class="sale-stat-container">
        <!-- 搜索条件弹窗 -->
        <Modal
            v-model="mainTable.searchCardShow"
            title="搜索条件"
            :width="1000"
            footer-hide
            :mask-closable="false"
        >
            <search-card
                :form="mainTable.searchForm"
                :table-load-event="debouncedGetSaleStatList"
                :table-search="mainTable.tableSearch"
            />
        </Modal>

        <!-- 主表格卡片 -->
        <Card class="main-card">
            <!-- 工具栏 -->
            <Row :gutter="16">
                <Col span="24">
                    <div class="global-tool-bar">
                        <div class="left-bar">
                            <span class="title">配送费包装费统计</span>
                            
                            <!-- 会员类别选择 -->
                            <span class="filter-label">会员类别:</span>
                            <RadioGroup
                                v-model="memberType"
                                class="filter-radio-group"
                                @on-change="handleMemberTypeChange"
                            >
                                <Radio label="F">
                                    <span>外部会员</span>
                                </Radio>
                                <Radio label="E">
                                    <span>内部会员</span>
                                </Radio>
                            </RadioGroup>
                            
                            <!-- 统计类别选择 -->
                            <span class="filter-label">统计类别:</span>
                            <RadioGroup
                                v-model="opUserType"
                                class="filter-radio-group"
                                @on-change="handleStatTypeChange"
                            >
                                <Radio label="B">
                                    <span>苗场</span>
                                </Radio>
                                <Radio label="D">
                                    <span>转运站</span>
                                </Radio>
                            </RadioGroup>
                            
                            <!-- 时间选择 -->
                            <span class="filter-label">统计时间:</span>
                            <DatePicker
                                type="daterange"
                                v-model="selectDates"
                                size="small"
                                transfer
                                placement="bottom-end"
                                placeholder="选择期初和期末时间"
                                class="date-picker"
                                @on-change="handleDateChange"
                                :disabled-date="disabledDate"
                            ></DatePicker>
                        </div>
                        
                        <div class="right-bar">
                            <Button
                                type="info"
                                size="small"
                                class="export-btn"
                                shape="circle"
                                icon="ios-send"
                                :loading="isMainLoading"
                                :disabled="!canExport"
                                @click="handleMainTableExport"
                            >
                                导出
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            <!-- 主表格 -->
            <Row :gutter="16">
                <Col span="24">
                    <div v-if="isMainLoading && mainTable.data.length === 0" class="loading-container">
                        <Spin size="large">
                            <Icon type="ios-loading" size="18" class="spin-icon-load"></Icon>
                            <div>加载中...</div>
                        </Spin>
                    </div>
                    <div v-else-if="mainTable.data.length === 0" class="empty-container">
                        <Icon type="ios-document-outline" size="48" class="empty-icon" />
                        <p class="empty-text">暂无统计数据</p>
                    </div>
                    <stand-pagination-table
                        v-else
                        :columns="mainTable.columns"
                        :data="mainTable.data"
                        :loading="mainTable.loading"
                        :table-search="mainTable.tableSearch"
                        :extHeight="-50"
                        :table-load-event="debouncedGetSaleStatList"
                        :showPage="false"
                    />
                </Col>
            </Row>
        </Card>

        <!-- 运费明细弹窗 -->
        <Modal
            v-model="deliveryDetailModal.show"
            title="运费明细"
            :width="925"
            :closable="false"
            footer-hide
            :mask-closable="false"
        >
            <div slot="header">
                <span class="modal-title">运费明细</span>
                <Button
                    type="info"
                    size="small"
                    class="export-btn"
                    shape="circle"
                    icon="ios-send"
                    :loading="isDetailLoading"
                    :disabled="!canExportDetail"
                    @click="handleDetailExport"
                >
                    导出
                </Button>
                <div class="modal-close">
                    <Button class="close-btn" type="text" @click="closeDetailModal">
                        ✕
                    </Button>
                </div>
            </div>
            
            <Row :gutter="16">
                <Col span="24">
                    <div v-if="isDetailLoading && deliveryDetailModal.data.length === 0" class="loading-container">
                        <Spin size="large">
                            <Icon type="ios-loading" size="18" class="spin-icon-load"></Icon>
                            <div>加载中...</div>
                        </Spin>
                    </div>
                    <div v-else-if="deliveryDetailModal.data.length === 0" class="empty-container">
                        <Icon type="ios-document-outline" size="48" class="empty-icon" />
                        <p class="empty-text">暂无明细数据</p>
                    </div>
                    <stand-pagination-table
                        v-else
                        :columns="deliveryDetailModal.columns"
                        :data="deliveryDetailModal.data"
                        :loading="deliveryDetailModal.loading"
                        :table-search="deliveryDetailModal.tableSearch"
                        :table-load-event="handleDetailPageChange"
                        :autoHeight="true"
                        :showPage="true"
                    />
                </Col>
            </Row>
        </Modal>

        <!-- 预销售配送详情组件 -->
        <PreSaleDeliveryDetail ref="preSaleDeliveryDetailModal" />
    </div>
</template>

<script>
import SearchCard from "@/components/searchCard/searchCard.vue";
import search from "@/libs/search";
import StandPaginationTable from "@/components/standard-pagination-table/StandPaginationTable";
import { dateTimeRender } from "@/libs/tableRowTimeRender";
import {
    numberToFixed,
    number2ThousandsFormat
} from "@/components/number-input/display-format";
import { getThisMonthStart, getTodayDateString } from "@/libs/logic";
import {
    getDeliverySaleStat,
    getDeliverySaleStatDetail
} from "@/api/modules/sale-management/sale-stat";
import { DOWNLOAD_SERVER } from "@/api/modules/files";
import PreSaleDeliveryDetail from "@/view/preSale-management/preSale-delivery/preSale-delivery-detail";

import { debounce } from "lodash";

// 配置常量
const CONSTANTS = {
    DEBOUNCE_DELAY: 500,
    DATE_FORMAT: 'YYYY-MM-DD',
    MAX_DATE_RANGE: 365,
    DEFAULT_PAGE_SIZE: 10,
    MODAL_WIDTH: 800,
    SEARCH_MODAL_WIDTH: 1000
};

export default {
    name: "SaleStationStatList",
    components: {
        StandPaginationTable,
        SearchCard,
        PreSaleDeliveryDetail
    },
    data() {
        return {
            // 筛选条件
            selectDates: [],
            memberType: "F",
            opUserType: "B",
            userType: "F",
            id: '',
            
            // 运费明细弹窗
            deliveryDetailModal: {
                opUserUuid: "",
                show: false,
                loading: false,
                tableSearch: search.getSearchParams(),
                data: [],
                columns: this.getDetailTableColumns()
            },
            
            // 主表格
            mainTable: {
                columns: this.getMainTableColumns(),
                data: [],
                searchCardShow: false,
                searchForm: [],
                loading: false,
                tableSearch: search.getSearchParams()
            }
        };
    },
    computed: {
        // 缓存用户类型
        cachedUserType() {
            return this.userType;
        },
        
        // 检查是否可以导出
        canExport() {
            return this.mainTable.data.length > 0 && !this.mainTable.loading;
        },
        
        // 检查是否可以导出明细
        canExportDetail() {
            return this.deliveryDetailModal.data.length > 0 && !this.deliveryDetailModal.loading;
        },
        
        // 日期范围是否有效
        isValidDateRange() {
            return this.selectDates && this.selectDates.length === 2;
        },
        
        // 明细表格加载状态
        isDetailLoading() {
            return this.deliveryDetailModal.loading;
        },
        
        // 主表格加载状态
        isMainLoading() {
            return this.mainTable.loading;
        }
    },
    created() {
        // 初始化防抖函数
        this.debouncedGetSaleStatList = debounce(this.getSaleStatList, CONSTANTS.DEBOUNCE_DELAY);
    },
    
    beforeDestroy() {
        // 取消防抖函数
        if (this.debouncedGetSaleStatList && this.debouncedGetSaleStatList.cancel) {
            this.debouncedGetSaleStatList.cancel();
        }
    },
    mounted() {
        this.initializeDates();
        this.init();
    },
    methods: {
        // 工具方法
        getTodayDateString,
        getThisMonthStart,
        
        // ==================== 生命周期和初始化方法 ====================
        
        /**
         * 初始化日期范围
         */
        initializeDates() {
            const beginDate = this.getThisMonthStart();
            const endDate = this.getTodayDateString();
            this.selectDates = [beginDate, endDate];
        },
        
        /**
         * 初始化数据
         */
        init() {
            this.getSaleStatList();
        },
        
        // ==================== 表格列配置方法 ====================
        
        /**
         * 获取主表格列配置
         */
        getMainTableColumns() {
            return [
                { 
                    title: "名称", 
                    key: "name", 
                    width: 180, 
                    align: "center",
                    tooltip: true
                },
                { 
                    title: "类型", 
                    key: "type", 
                    width: 180, 
                    align: "center",
                    tooltip: true
                },
                this.createFeeColumn("配送费", "deliveryFee", '#515a6e'),
                this.createFeeColumn("包装费", "packageFee", '#515a6e'),
                {
                    title: "操作",
                    key: "action",
                    align: "center",
                    width: 80,
                    render: (h, params) => {
                        if (params.row.action !== -1) {
                            return h("div", [
                                h(
                                    "Button",
                                    {
                                        props: {
                                            type: "primary",
                                            size: "small",
                                            ghost: true
                                        },
                                        on: {
                                            click: () => {
                                                this.handleShowDeliveryDetail(
                                                    params.row.opUserUuid, 
                                                    params.row.id
                                                );
                                            }
                                        }
                                    },
                                    "查看明细"
                                )
                            ]);
                        }
                        return h("span", { class: "action-disabled" }, "-");
                    }
                }
            ];
        },
        
        /**
         * 创建费用列配置
         * @param {String} title - 列标题
         * @param {String} key - 数据键
         * @param {String} color - 正值颜色
         * @param {Number} width - 列宽度
         * @returns {Object} - 列配置
         */
        createFeeColumn(title, key, color, width = 120) {
            return {
                title,
                key,
                align: "right",
                width,
                sortable: true,
                excel: true,
                render: (h, params) => {
                    const fee = params.row[key] || 0;
                    return h("div", {}, [
                        h(
                            "span",
                            {
                                style: {
                                    color: fee > 0 ? color : '#c5c8ce',
                                    fontWeight: fee > 0 ? '500' : 'normal'
                                }
                            },
                            number2ThousandsFormat(fee, 2, "￥")
                        )
                    ]);
                }
            };
        },
        
        /**
         * 获取明细表格列配置
         */
        getDetailTableColumns() {
            return [
                this.createTextColumn("客户账号", "customer", 120),
                this.createTextColumn("客户姓名", "customerPersonName", 120),
                this.createOrderCodeColumn(),
                this.createFeeColumn("配送费", "deliveryFee", '#515a6e', 100),
                this.createFeeColumn("包装费", "packageFee", '#515a6e', 100),
                this.createFeeColumn("手动录入配送费", "farmDeliveryFee", '#ed4014', 120),
                this.createFeeColumn("手动录入包装费", "farmPackageFee", '#ed4014', 120)
            ];
        },
        
        /**
         * 创建文本列配置
         * @param {String} title - 列标题
         * @param {String} key - 数据键
         * @param {Number} width - 列宽度
         * @returns {Object} - 列配置
         */
        createTextColumn(title, key, width) {
            return {
                title,
                key,
                width,
                align: "center",
                excel: true,
                tooltip: true
            };
        },
        
        /**
         * 创建订单号列配置
         * @returns {Object} - 列配置
         */
        createOrderCodeColumn() {
            return {
                title: "销售单号",
                key: "orderCode",
                width: 200,
                align: "center",
                excel: true,
                render: (h, params) => {
                    return h(
                        "a",
                        {
                            style: {
                                color: '#2d8cf0',
                                cursor: 'pointer',
                                textDecoration: 'none'
                            },
                            on: {
                                click: () => {
                                    this.handleShowOrderDetail(
                                        params.row.orderUuid
                                    );
                                }
                            }
                        },
                        params.row.orderCode
                    );
                }
            };
        },
        
        // ==================== 事件处理方法 ====================
        
        /**
         * 日期变更处理
         * @param {Array} dates - 选择的日期范围
         */
        handleDateChange(dates) {
            if (!dates || dates.length !== 2) {
                this.$Message.warning('请选择完整的日期范围');
                return;
            }
            
            // 验证日期范围
            if (!this.validateDateRange(dates)) {
                return;
            }
            
            this.selectDates = dates;
            this.debouncedGetSaleStatList();
        },
        
        /**
         * 会员类别变更处理
         */
        handleMemberTypeChange() {
            this.debouncedGetSaleStatList();
        },
        
        /**
         * 统计类别变更处理
         */
        handleStatTypeChange() {
            this.debouncedGetSaleStatList();
        },
        
        /**
         * 日期选择器禁用日期处理
         * @param {Date} date - 当前日期
         */
        disabledDate(date) {
            // 禁用今天之后的日期
            return date && date > new Date();
        },
        
        /**
         * 验证日期范围
         * @param {Array} dates - 日期范围
         * @returns {Boolean} - 验证结果
         */
        validateDateRange(dates) {
            if (!dates || dates.length !== 2) {
                return false;
            }
            
            const startDate = new Date(dates[0]);
            const endDate = new Date(dates[1]);
            const diffDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
            
            if (diffDays > CONSTANTS.MAX_DATE_RANGE) {
                this.$Message.warning(`日期范围不能超过${CONSTANTS.MAX_DATE_RANGE}天`);
                return false;
            }
            
            if (startDate > endDate) {
                this.$Message.warning('开始日期不能大于结束日期');
                return false;
            }
            
            return true;
        },
        
        // ==================== 明细弹窗相关方法 ====================
        
        /**
         * 关闭明细弹窗
         */
        closeDetailModal() {
            this.resetDetailModalState();
            this.deliveryDetailModal.show = false;
        },
        
        /**
         * 重置明细弹窗状态
         */
        resetDetailModalState() {
            this.deliveryDetailModal.tableSearch.limit.conditions.pageStart = 1;
            this.deliveryDetailModal.tableSearch.limit.conditions.pageLength = CONSTANTS.DEFAULT_PAGE_SIZE;
            this.deliveryDetailModal.data = [];
            this.deliveryDetailModal.opUserUuid = "";
        },
        
        /**
         * 显示运费明细
         * @param {String} opUserUuid - 操作用户UUID
         */
        handleShowDeliveryDetail(opUserUuid, id) {
            if (!opUserUuid) {
                this.$Message.error('缺少必要的参数');
                return;
            }
            this.id = id;
            this.deliveryDetailModal.show = true;
            this.loadDeliveryDetailData(opUserUuid, id);
        },
        
        /**
         * 加载明细数据
         * @param {String} opUserUuid - 操作用户UUID
         */
        async loadDeliveryDetailData(opUserUuid) {
            try {
                this.deliveryDetailModal.loading = true;
                
                const searchParams = this.prepareDetailTableSearchParams(opUserUuid);
                const response = await getDeliverySaleStatDetail(searchParams);
                
                if (response.code === 0) {
                    this.deliveryDetailModal.data = response.data.listPreSaleOrder || [];
                    this.updateFilterRecordCount(response.data.filterRecordCount || 0);
                }
                else {
                    this.$Message.error(response.message || '获取明细数据失败');
                }
            }
            catch (error) {
                console.error('获取明细数据失败:', error);
                this.$Message.error('获取明细数据失败，请重试');
            }
            finally {
                this.deliveryDetailModal.loading = false;
            }
        },
        
        /**
         * 明细表格分页变更处理
         */
        handleDetailPageChange() {
            this.loadDeliveryDetailData(this.deliveryDetailModal.opUserUuid);
        },
        
        /**
         * 显示订单详情
         * @param {String} orderUuid - 订单UUID
         */
        handleShowOrderDetail(orderUuid) {
            if (!orderUuid) {
                this.$Message.error('订单参数缺失');
                return;
            }
            
            if (this.$refs.preSaleDeliveryDetailModal) {
                this.$refs.preSaleDeliveryDetailModal.showModal(orderUuid);
            }
            else {
                this.$Message.error('订单详情组件未加载');
            }
        },
        
        // ==================== 导出相关方法 ====================
        
        /**
         * 主表格导出
         */
        handleMainTableExport() {
            if (!this.canExport) {
                this.$Message.warning('没有可导出的数据');
                return;
            }
            
            this.prepareMainTableSearchParams();
            this.handleExport(
                this.mainTable.tableSearch,
                this.mainTable.columns,
                (loading) => {
                    this.mainTable.loading = loading;
                }
            );
        },
        
        /**
         * 明细表格导出
         */
        handleDetailExport() {
            if (!this.canExportDetail) {
                this.$Message.warning('没有可导出的明细数据');
                return;
            }
            
            this.handleExport(
                this.deliveryDetailModal.tableSearch,
                this.deliveryDetailModal.columns,
                (loading) => {
                    this.deliveryDetailModal.loading = loading;
                }
            );
        },
        
        /**
         * 统一的导出方法
         * @param {Object} searchParams - 搜索参数
         * @param {Array} columns - 列配置
         * @param {Function} setLoading - 设置加载状态的函数
         */
        async handleExport(searchParams, columns, setLoading) {
            try {
                setLoading(true);
                
                const searchCondition = this.prepareExportSearchParams(searchParams, columns);
                const response = await this.getExportData(searchCondition);
                
                if (response.code === 0) {
                    const downloadUrl = DOWNLOAD_SERVER + response.data.exportFileName;
                    this.$downLoadFile(downloadUrl);
                    this.$Message.success('导出成功');
                }
                else {
                    this.$Message.error(response.message || '导出失败');
                }
            }
            catch (error) {
                console.error('导出失败:', error);
                this.$Message.error('导出失败，请重试');
            }
            finally {
                setLoading(false);
            }
        },
        
        /**
         * 准备导出参数
         * @param {Object} searchParams - 搜索参数
         * @param {Array} columns - 列配置
         * @returns {Object} - 导出参数
         */
        prepareExportSearchParams(searchParams, columns) {
            const searchCondition = search.deepClone(searchParams);
            
            // 配置导出参数
            searchCondition.limit.enable = false;
            searchCondition.excelExport = {
                enable: true,
                columns: columns
                    .filter((col) => col.excel)
                    .map((col) => ({
                        key: col.key,
                        title: col.title
                    }))
            };
            
            // 明细表格需要额外设置导出标志
            if (searchParams === this.deliveryDetailModal.tableSearch) {
                searchCondition.excelExport.enable = true;
            }
            else {
                searchCondition.needExport = 1;
            }
            
            return searchCondition;
        },
        
        /**
         * 获取导出数据
         * @param {Object} searchCondition - 搜索条件
         * @returns {Promise} - API响应
         */
        getExportData(searchCondition) {
            if (searchCondition.excelExport.enable) {
                return getDeliverySaleStatDetail(searchCondition);
            }
            else {
                return getDeliverySaleStat(searchCondition);
            }
        },
        
        // ==================== 数据处理方法 ====================
        
        /**
         * 准备主表格搜索参数
         */
        prepareMainTableSearchParams() {
            this.mainTable.tableSearch.userType = this.cachedUserType;
            this.mainTable.tableSearch.memberType = this.memberType;
            this.mainTable.tableSearch.opUserType = this.opUserType;
            this.mainTable.tableSearch.needExport = 0;
            this.mainTable.tableSearch.beginDate = this.selectDates[0];
            this.mainTable.tableSearch.endDate = this.selectDates[1];
        },
        
        /**
         * 准备明细表格搜索参数
         * @param {String} opUserUuid - 操作用户UUID
         * @returns {Object} - 搜索参数
         */
        prepareDetailTableSearchParams(opUserUuid) {
            const tempSearch = this.deliveryDetailModal.tableSearch;
            tempSearch.memberType = this.memberType;
            tempSearch.opUserType = this.opUserType;
            tempSearch.excelExport.enable = false;
            
            // 将日期都加1天并格式化
            tempSearch.beginDate = this.transfer(this.selectDates[0]);
            tempSearch.endDate = this.transfer(this.selectDates[1]);
            
            if (opUserUuid) {
                tempSearch.opUserUuid = opUserUuid;
                tempSearch.id = this.id;
                this.deliveryDetailModal.opUserUuid = opUserUuid;
            }
            else {
                tempSearch.opUserUuid = this.deliveryDetailModal.opUserUuid;
                tempSearch.id = this.id;
            }
            
            return tempSearch;
        },
        
        /**
         * 格式化
         * @param {String} dateString - 日期字符串
         * @returns {String} 
         */
        transfer(dateString) {
            if (!dateString) return '';
            
            const date = new Date(dateString);
            date.setDate(date.getDate());
            
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            
            return `${year}-${month}-${day}`;
        },
        
        /**
         * 更新过滤记录数
         * @param {Number} filterRecordCount - 过滤记录数
         */
        updateFilterRecordCount(filterRecordCount) {
            search.globalSetFilterRecordCount(
                this.deliveryDetailModal.tableSearch,
                filterRecordCount
            );
        },
        
        /**
         * 计算统计数据
         * @param {Array} data - 数据数组
         * @returns {Object} - 合计数据
         */
        calculateSummaryData(data) {
            if (!Array.isArray(data) || data.length === 0) {
                return this.createEmptySummary();
            }
            
            const summary = data.reduce((acc, item) => {
                acc.deliveryFeeAll += Number(item.deliveryFee || 0);
                acc.packageFeeAll += Number(item.packageFee || 0);
                return acc;
            }, { deliveryFeeAll: 0, packageFeeAll: 0 });
            
            return {
                type: "",
                action: -1,
                deliveryFee: numberToFixed(summary.deliveryFeeAll, 2),
                packageFee: numberToFixed(summary.packageFeeAll, 2),
                name: "合计"
            };
        },
        
        /**
         * 创建空合计数据
         * @returns {Object} - 空合计数据
         */
        createEmptySummary() {
            return {
                type: "合计",
                action: -1,
                deliveryFee: numberToFixed(0, 2),
                packageFee: numberToFixed(0, 2),
                name: "合计"
            };
        },
        
        // ==================== 数据获取方法 ====================
        
        /**
         * 获取销售统计数据
         */
        async getSaleStatList() {
            if (!this.validateSearchConditions()) {
                return;
            }
            
            try {
                this.mainTable.loading = true;
                this.prepareMainTableSearchParams();
                
                const response = await getDeliverySaleStat(this.mainTable.tableSearch);
                
                if (response.code === 0) {
                    this.handleStatDataResponse(response.data);
                }
                else {
                    this.$Message.error(response.message || '获取统计数据失败');
                }
            }
            catch (error) {
                console.error('获取统计数据失败:', error);
                this.$Message.error('获取统计数据失败，请重试');
            }
            finally {
                this.mainTable.loading = false;
            }
        },
        
        /**
         * 验证搜索条件
         * @returns {Boolean} - 验证结果
         */
        validateSearchConditions() {
            if (!this.selectDates || this.selectDates.length !== 2) {
                this.$Message.warning('请选择日期范围');
                return false;
            }
            
            return this.validateDateRange(this.selectDates);
        },
        
        /**
         * 处理统计数据响应
         * @param {Object} data - 响应数据
         */
        handleStatDataResponse(data) {
            const deliveryFeeData = data.deliveryFee || [];
            
            // 添加合计行
            const summaryData = this.calculateSummaryData(deliveryFeeData);
            this.mainTable.data = [...deliveryFeeData, summaryData];
        }
    }
};
</script>

<style scoped>
/* 工具栏样式 */
.global-tool-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding: 12px 16px;
    background-color: #f8f9fa;
    border-radius: 4px;
}

.left-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
}

.title {
    font-size: 16px;
    font-weight: 600;
    color: #17233d;
    margin-right: 8px;
}

.filter-label {
    color: #515a6e;
    font-size: 14px;
}

.filter-radio-group {
    margin-left: 4px;
}

.date-picker {
    margin-left: 4px;
    width: 200px;
}

.right-bar {
    display: flex;
    align-items: center;
}

.export-btn {
    min-width: 80px;
}

/* 弹窗样式 */
.modal-title {
    font-size: 14px;
    font-weight: 600;
    color: #17233d;
}

.modal-close {
    display: flex;
    justify-content: flex-end;
    margin-top: -20px;
}

.close-btn {
    font-size: 18px;
    line-height: 0 !important;
    color: #808695;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.3s ease;
}

.close-btn:hover {
    color: #17233d;
    background-color: #f8f9fa;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .global-tool-bar {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
        padding: 8px;
    }
    
    .left-bar {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
    
    .right-bar {
        justify-content: center;
        margin-top: 12px;
    }
    
    .title {
        text-align: center;
        margin-bottom: 12px;
        font-size: 18px;
    }
    
    .filter-label {
        font-size: 13px;
        margin-bottom: 4px;
    }
    
    .filter-radio-group {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    
    .date-picker {
        width: 100%;
    }
    
    .export-btn {
        width: 100%;
        margin-top: 8px;
    }
    
    .ivu-table {
        font-size: 12px;
    }
    
    .ivu-table-header th {
        font-size: 13px;
        padding: 8px 4px;
    }
    
    .ivu-table td {
        padding: 8px 4px;
    }
    
    .ivu-btn {
        font-size: 12px;
    }
}

@media (max-width: 480px) {
    .global-tool-bar {
        padding: 6px;
    }
    
    .title {
        font-size: 16px;
    }
    
    .filter-label {
        font-size: 12px;
    }
    
    .ivu-table {
        font-size: 11px;
    }
    
    .ivu-table-header th {
        font-size: 12px;
        padding: 6px 2px;
    }
    
    .ivu-table td {
        padding: 6px 2px;
    }
    
    .ivu-btn {
        font-size: 11px;
        padding: 4px 8px;
    }
}

/* 表格样式优化 */
.ivu-table {
    border-radius: 4px;
    overflow: hidden;
}

.ivu-table-header {
    background-color: #f8f9fa;
}

.ivu-table-header th {
    background-color: #f8f9fa;
    color: #515a6e;
    font-weight: 600;
}

.ivu-table-tbody tr:hover {
    background-color: #f0f9ff;
}

/* 加载状态样式 */
.ivu-btn-loading {
    pointer-events: none;
    opacity: 0.6;
}

/* 卡片样式 */
.ivu-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.ivu-card-body {
    padding: 16px;
}

/* 按钮样式统一 */
.ivu-btn {
    transition: all 0.3s ease;
}

.ivu-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.ivu-btn:active {
    transform: translateY(0);
}

/* 单选框样式优化 */
.ivu-radio-group {
    display: inline-flex;
    align-items: center;
}

.ivu-radio-wrapper {
    margin-right: 16px;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.3s ease;
}

.ivu-radio-wrapper:hover {
    background-color: #f0f9ff;
}

/* 日期选择器样式 */
.ivu-date-picker {
    display: inline-block;
}

.ivu-date-picker .ivu-input {
    border-radius: 4px;
    transition: all 0.3s ease;
}

.ivu-date-picker .ivu-input:hover {
    border-color: #57a3f3;
}

.ivu-date-picker .ivu-input:focus {
    border-color: #57a3f3;
    box-shadow: 0 0 0 2px rgba(87, 163, 243, 0.2);
}
</style>
